import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFontSize, FontSize } from '../hooks/useFontSize';
import '../styles/pages/guestCollectionView.css';
import '../styles/header.css';
import '../styles/components/unifiedHeader.css';
import '../styles/pages/auctionDetailPage.css';
import API_BASE_URL from '../config';

interface MyBid {
  bid_id: number;
  auction_item_id: number;
  amount: number;
  comment: string | null;
  placed_at: string;
  result: string;
  collection_deadline: string | null;
  item_name: string;
  item_description: string | null;
  image_id: number | null;
  auction_status: string;
  ends_at: string;
  seller_name: string;
}

const centsToDisplay = (cents: number) => (cents / 100).toFixed(2);

const formatTimeRemaining = (endsAt: string): string => {
  const now = new Date();
  const end = new Date(endsAt);
  const diff = end.getTime() - now.getTime();
  if (diff <= 0) return 'Ended';
  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

const resultLabel = (result: string, auctionStatus: string): { text: string; color: string } => {
  if (auctionStatus === 'active') return { text: 'Active', color: '#059669' };
  switch (result) {
    case 'winner': return { text: 'Winner!', color: '#059669' };
    case 'collected': return { text: 'Collected', color: '#374151' };
    case 'outbid': return { text: 'Outbid', color: '#6b7280' };
    case 'runner_up': return { text: 'Runner-up', color: '#2563eb' };
    case 'forfeited': return { text: 'Forfeited', color: '#dc2626' };
    case 'below_reserve': return { text: 'Below Reserve', color: '#d97706' };
    case 'withdrawn': return { text: 'Withdrawn', color: '#9ca3af' };
    default: return { text: result || 'Pending', color: '#6b7280' };
  }
};

const MyBidsPage: React.FC = () => {
  const navigate = useNavigate();
  const { fontSize, setFontSize } = useFontSize();
  const token = localStorage.getItem('auth_token');

  const [bids, setBids] = useState<MyBid[]>([]);
  const [loading, setLoading] = useState(true);

  // Guest theme — default to system preference, not the logged-in user's stored theme
  const [guestTheme, setGuestTheme] = useState<'light' | 'dark'>(() => {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });
  const savedThemePrefRef = React.useRef<string | null>(null);
  useEffect(() => {
    savedThemePrefRef.current = localStorage.getItem('theme-preference');
    localStorage.setItem('theme-preference', 'system');
    const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', systemTheme);
    return () => {
      if (savedThemePrefRef.current !== null) {
        localStorage.setItem('theme-preference', savedThemePrefRef.current);
      } else {
        localStorage.removeItem('theme-preference');
      }
      document.documentElement.setAttribute('data-theme',
        savedThemePrefRef.current === 'dark' ? 'dark'
          : savedThemePrefRef.current === 'light' ? 'light'
          : savedThemePrefRef.current === 'pastel' ? 'pastel'
          : window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
      );
    };
  }, []);
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', guestTheme);
  }, [guestTheme]);

  const fetchBids = useCallback(async () => {
    if (!token) { navigate('/', { replace: true }); return; }
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/auction-items/bids/mine`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch bids');
      const data = await response.json();
      setBids(data || []);
    } catch (err) {
      console.error('Error fetching my bids:', err);
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    fetchBids();
  }, [fetchBids]);

  return (
    <div className="guest-collection-view">
      {/* Header */}
      <div
        ref={(el) => {
          if (el) {
            const h = el.getBoundingClientRect().height;
            document.documentElement.style.setProperty('--guest-header-h', `${Math.ceil(h)}px`);
          }
        }}
        style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1000, background: 'var(--color-bg-primary)', borderBottom: '1px solid var(--color-border)' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 1rem', position: 'relative' }}>
          <button
            onClick={() => navigate(-1)}
            style={{ background: 'transparent', border: 'none', padding: '8px', cursor: 'pointer', color: 'var(--color-text-primary)', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"></polyline>
            </svg>
            <span style={{ fontSize: '14px' }}>Back</span>
          </button>
          <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
            <div className="unified-header-title">My Bids</div>
          </div>
          <div className="unified-header-actions">
            <button
              onClick={() => {
                const sizes: FontSize[] = ['small', 'medium', 'large', 'x-large'];
                const currentIndex = sizes.indexOf(fontSize);
                const nextIndex = (currentIndex + 1) % sizes.length;
                setFontSize(sizes[nextIndex]);
              }}
              className="unified-header-font-toggle"
              title={`Font size: ${fontSize}`}
              aria-label="Change font size"
            >
              <span className="font-toggle-text">Aa</span>
              <span className="font-toggle-bars">
                <span className={`bar bar-1 ${['small','medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-2 ${['medium','large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-3 ${['large','x-large'].indexOf(fontSize) >= 0 ? 'active' : ''}`}></span>
                <span className={`bar bar-4 ${fontSize === 'x-large' ? 'active' : ''}`}></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="guest-content">
        {loading ? (
          <div className="marketplace-loading">Loading your bids...</div>
        ) : bids.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 20px', color: 'var(--color-text-muted)' }}>
            <p>You haven't placed any bids yet.</p>
            <p style={{ fontSize: '14px', marginTop: 8 }}>
              Browse the{' '}
              <button
                onClick={() => navigate('/marketplace')}
                style={{ background: 'none', border: 'none', color: 'var(--color-accent)', cursor: 'pointer', fontSize: 'inherit', textDecoration: 'underline' }}
              >
                Marketplace
              </button>{' '}
              to find auctions.
            </p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {bids.map((bid) => {
              const status = resultLabel(bid.result, bid.auction_status);
              return (
                <div
                  key={bid.bid_id}
                  className="auction-card"
                  onClick={() => navigate(`/marketplace/auction/${bid.auction_item_id}`)}
                >
                  <div className="auction-card-image">
                    {bid.image_id ? (
                      <img src={`${API_BASE_URL}/api/images/${bid.image_id}/public`} alt={bid.item_name} />
                    ) : (
                      <div className="auction-card-placeholder">
                        {bid.item_name.substring(0, 2).toUpperCase()}
                      </div>
                    )}
                    <div className="auction-card-badge" style={{ background: status.color }}>
                      {status.text}
                    </div>
                  </div>
                  <div className="auction-card-info">
                    <div className="auction-card-name">{bid.item_name}</div>
                    <div className="auction-card-price">
                      Your bid: ${centsToDisplay(bid.amount)}
                    </div>
                    <div className="auction-card-meta">
                      {bid.auction_status === 'active'
                        ? `${formatTimeRemaining(bid.ends_at)} left`
                        : `Ended`}
                      {bid.collection_deadline && bid.result === 'winner' && (
                        <span> · Collect by {new Date(bid.collection_deadline).toLocaleDateString()}</span>
                      )}
                    </div>
                    <div className="auction-card-seller">
                      by {bid.seller_name}
                    </div>
                    {bid.comment && (
                      <div style={{ fontSize: '12px', color: 'var(--color-text-muted)', marginTop: 2, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        "{bid.comment}"
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyBidsPage;
